'use client';

import { useState, useMemo, useEffect } from 'react';
import SeverityBadge, { Severity } from '@/components/ui/SeverityBadge';
import EventDetailDrawer from './EventDetailDrawer';
import Icon from '@/components/ui/AppIcon';

type Protocol = 'TCP' | 'UDP' | 'ICMP' | 'HTTP' | 'HTTPS' | 'DNS' | 'SMTP';
type EventType = 'Brute Force' | 'SQL Injection' | 'Port Scan' | 'DNS Tunneling' | 'C2 Beacon' | 'XSS Attempt' | 'Auth Failure' | 'Lateral Movement' | 'RDP Brute Force' | 'LFI Attempt' | 'DoS Attack' | 'Privilege Escalation';

interface SecurityEvent {
  id: string;
  timestamp: string;
  srcIp: string;
  srcPort: number;
  dstIp: string;
  dstPort: number;
  protocol: Protocol;
  eventType: EventType;
  severity: Severity;
  asset: string;
  rule: string;
  country: string;
  bytes: number;
  status: 'new' | 'acknowledged' | 'closed';
}

const protocols: Protocol[] = ['TCP', 'UDP', 'ICMP', 'HTTP', 'HTTPS', 'DNS', 'SMTP'];
const eventTypes: EventType[] = ['Brute Force', 'SQL Injection', 'Port Scan', 'DNS Tunneling', 'C2 Beacon', 'XSS Attempt', 'Auth Failure', 'Lateral Movement', 'RDP Brute Force', 'LFI Attempt', 'DoS Attack', 'Privilege Escalation'];
const severities: Severity[] = ['critical', 'critical', 'high', 'high', 'medium', 'medium', 'medium', 'low', 'low', 'info'];
const assets = ['auth-server-01', 'webapp-prod-01', 'webapp-prod-02', 'core-dns-01', 'dmz-fw-01', 'rdp-gateway-01', 'workstation-hr-14', 'workstation-dev-07', 'db-server-02', 'mail-server-01', 'vpn-gateway-01', 'proxy-server-01'];
const countries = ['RU', 'CN', 'IN', 'UA', 'US', 'DE', 'BR', 'KP', 'IR', 'NL'];
const rules = ['SSH-BF-001', 'SQLI-WEB-004', 'SCAN-PORT-002', 'DNS-TUNNEL-001', 'C2-BEACON-007', 'XSS-WEB-012', 'AUTH-FAIL-003', 'LATERAL-003', 'RDP-BF-002', 'LFI-WEB-006', 'DOS-DETECT-001', 'PRIV-ESC-004'];

const srcIps = ['185.220.101.47', '45.133.1.57', '103.75.246.18', '91.108.4.23', '198.51.100.8', '77.88.55.80', '192.0.2.14', '203.0.113.12', '10.0.5.201', '10.0.8.113', '172.16.4.33', '172.16.8.21'];
const dstIps = ['10.0.1.15', '10.0.4.22', '10.0.2.8', '10.0.1.5', '10.0.3.17', '10.0.6.9', '10.0.7.4', '10.0.1.1', '10.0.5.12', '10.0.9.3'];

function generateEvents(): SecurityEvent[] {
  // Backend integration: Replace with GET /api/events?page=1&limit=50&filters=...
  return Array.from({ length: 120 }, (_, i) => {
    const sev = severities[Math.floor(Math.random() * severities.length)];
    const proto = protocols[Math.floor(Math.random() * protocols.length)];
    const type = eventTypes[Math.floor(Math.random() * eventTypes.length)];
    const m = String(Math.floor(Math.random() * 60)).padStart(2, '0');
    const s = String(Math.floor(Math.random() * 60)).padStart(2, '0');
    return {
      id: `EVT-${String(9182 - i).padStart(6, '0')}`,
      timestamp: `2026-03-16 07:${m}:${s} UTC`,
      srcIp: srcIps[Math.floor(Math.random() * srcIps.length)],
      srcPort: [22, 80, 443, 3389, 53, 25, 8080, 4444][Math.floor(Math.random() * 8)],
      dstIp: dstIps[Math.floor(Math.random() * dstIps.length)],
      dstPort: [22, 80, 443, 3306, 5432, 3389, 53][Math.floor(Math.random() * 7)],
      protocol: proto,
      eventType: type,
      severity: sev,
      asset: assets[Math.floor(Math.random() * assets.length)],
      rule: rules[Math.floor(Math.random() * rules.length)],
      country: countries[Math.floor(Math.random() * countries.length)],
      bytes: Math.floor(Math.random() * 50000 + 100),
      status: (['new', 'acknowledged', 'closed'] as const)[Math.floor(Math.random() * 3)],
    };
  });
}

const protocolColors: Record<Protocol, string> = {
  TCP: 'bg-blue-500/15 text-blue-400 border border-blue-500/20',
  UDP: 'bg-purple-500/15 text-purple-400 border border-purple-500/20',
  ICMP: 'bg-zinc-700/50 text-zinc-400 border border-zinc-700',
  HTTP: 'bg-amber-500/15 text-amber-400 border border-amber-500/20',
  HTTPS: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20',
  DNS: 'bg-orange-500/15 text-orange-400 border border-orange-500/20',
  SMTP: 'bg-rose-500/15 text-rose-400 border border-rose-500/20',
};

type SortKey = keyof SecurityEvent;

export default function EventMonitoringTable() {
  const [search, setSearch] = useState('');
  const [severityFilter, setSeverityFilter] = useState<Severity | 'all'>('all');
  const [protocolFilter, setProtocolFilter] = useState<Protocol | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<EventType | 'all'>('all');
  const [page, setPage] = useState(1);
  const [perPage] = useState(15);
  const [sortKey, setSortKey] = useState<SortKey>('id');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedEvent, setSelectedEvent] = useState<SecurityEvent | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [allEvents, setAllEvents] = useState<SecurityEvent[]>([]);

  useEffect(() => {
    setAllEvents(generateEvents());
  }, []);

  const filtered = useMemo(() => {
    let evts = allEvents;
    if (search) {
      const q = search.toLowerCase();
      evts = evts.filter((e) =>
        e.id.toLowerCase().includes(q) ||
        e.srcIp.includes(q) ||
        e.dstIp.includes(q) ||
        e.asset.toLowerCase().includes(q) ||
        e.eventType.toLowerCase().includes(q) ||
        e.rule.toLowerCase().includes(q)
      );
    }
    if (severityFilter !== 'all') evts = evts.filter((e) => e.severity === severityFilter);
    if (protocolFilter !== 'all') evts = evts.filter((e) => e.protocol === protocolFilter);
    if (typeFilter !== 'all') evts = evts.filter((e) => e.eventType === typeFilter);
    evts = [...evts].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return evts;
  }, [search, severityFilter, protocolFilter, typeFilter, sortKey, sortDir]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const pageData = filtered.slice((page - 1) * perPage, page * perPage);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const toggleRow = (id: string) => {
    setSelectedRows((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
  };

  const toggleAll = () => {
    if (selectedRows.size === pageData.length) setSelectedRows(new Set());
    else setSelectedRows(new Set(pageData.map((e) => e.id)));
  };

  const SortIcon = ({ col }: { col: SortKey }) => (
    <span className={`ml-1 inline-block transition-colors ${sortKey === col ? 'text-rose-400' : 'text-zinc-700'}`}>
      {sortKey === col ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
    </span>
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48">
          <Icon name="MagnifyingGlassIcon" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            placeholder="Search events, IPs, assets, rules..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-8 pr-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-sm text-zinc-300 placeholder-zinc-600 focus:outline-none focus:border-rose-500/50 focus:ring-1 focus:ring-rose-500/30"
          />
        </div>

        <select
          value={severityFilter}
          onChange={(e) => { setSeverityFilter(e.target.value as any); setPage(1); }}
          className="px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-xs text-zinc-400 focus:outline-none focus:border-rose-500/50 cursor-pointer"
        >
          <option value="all">All Severities</option>
          {(['critical', 'high', 'medium', 'low', 'info'] as Severity[]).map((s) => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>

        <select
          value={protocolFilter}
          onChange={(e) => { setProtocolFilter(e.target.value as any); setPage(1); }}
          className="px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-xs text-zinc-400 focus:outline-none focus:border-rose-500/50 cursor-pointer"
        >
          <option value="all">All Protocols</option>
          {protocols.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>

        <select
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value as any); setPage(1); }}
          className="px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-xs text-zinc-400 focus:outline-none focus:border-rose-500/50 cursor-pointer"
        >
          <option value="all">All Event Types</option>
          {eventTypes.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>

        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-zinc-600 font-mono">{filtered.length.toLocaleString()} events</span>
          <button className="flex items-center gap-1.5 px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-xs text-zinc-400 hover:text-zinc-200 hover:border-zinc-700 transition-colors">
            <Icon name="ArrowDownTrayIcon" size={13} />
            Export
          </button>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedRows.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-rose-500/10 border border-rose-500/20 rounded-lg animate-fade-in">
          <span className="text-sm font-medium text-rose-400">{selectedRows.size} event{selectedRows.size > 1 ? 's' : ''} selected</span>
          <div className="flex items-center gap-2 ml-auto">
            <button className="px-3 py-1.5 text-xs bg-zinc-900 border border-zinc-700 rounded-md text-zinc-400 hover:text-zinc-200 transition-colors">
              Acknowledge
            </button>
            <button className="px-3 py-1.5 text-xs bg-zinc-900 border border-zinc-700 rounded-md text-zinc-400 hover:text-zinc-200 transition-colors">
              Create Incident
            </button>
            <button className="px-3 py-1.5 text-xs bg-zinc-900 border border-zinc-700 rounded-md text-zinc-400 hover:text-zinc-200 transition-colors">
              Export Selected
            </button>
            <button
              onClick={() => setSelectedRows(new Set())}
              className="p-1.5 text-zinc-600 hover:text-zinc-400 transition-colors"
            >
              <Icon name="XMarkIcon" size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="px-3 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={selectedRows.size === pageData.length && pageData.length > 0}
                    onChange={toggleAll}
                    className="rounded border-zinc-700 bg-zinc-800 text-rose-500 focus:ring-rose-500/30"
                    aria-label="Select all"
                  />
                </th>
                {([
                  { key: 'id', label: 'Event ID' },
                  { key: 'timestamp', label: 'Timestamp (UTC)' },
                  { key: 'srcIp', label: 'Source IP' },
                  { key: 'srcPort', label: 'Src Port' },
                  { key: 'dstIp', label: 'Dest IP' },
                  { key: 'dstPort', label: 'Dst Port' },
                  { key: 'protocol', label: 'Protocol' },
                  { key: 'eventType', label: 'Event Type' },
                  { key: 'severity', label: 'Severity' },
                  { key: 'asset', label: 'Asset' },
                  { key: 'rule', label: 'Rule' },
                  { key: 'country', label: 'Geo' },
                  { key: 'bytes', label: 'Bytes' },
                ] as { key: SortKey; label: string }[]).map(({ key, label }) => (
                  <th
                    key={key}
                    onClick={() => handleSort(key)}
                    className="px-3 py-3 text-left font-semibold text-zinc-600 uppercase tracking-widest text-[10px] whitespace-nowrap cursor-pointer hover:text-zinc-400 transition-colors select-none"
                  >
                    {label}<SortIcon col={key} />
                  </th>
                ))}
                <th className="px-3 py-3 text-left font-semibold text-zinc-600 uppercase tracking-widest text-[10px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageData.map((evt, idx) => (
                <tr
                  key={evt.id}
                  className={`
                    border-b border-zinc-800/40 hover:bg-zinc-800/40 transition-colors duration-100 cursor-pointer group
                    ${selectedRows.has(evt.id) ? 'bg-rose-500/5' : idx % 2 === 0 ? '' : 'bg-zinc-900/50'}
                    ${evt.severity === 'critical' ? 'hover:bg-red-500/5' : ''}
                  `}
                  onClick={() => setSelectedEvent(evt)}
                >
                  <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedRows.has(evt.id)}
                      onChange={() => toggleRow(evt.id)}
                      className="rounded border-zinc-700 bg-zinc-800 text-rose-500 focus:ring-rose-500/30"
                      aria-label={`Select ${evt.id}`}
                    />
                  </td>
                  <td className="px-3 py-2.5 font-mono text-zinc-400 whitespace-nowrap">{evt.id}</td>
                  <td className="px-3 py-2.5 font-mono text-zinc-500 whitespace-nowrap text-[11px]">{evt.timestamp.split(' ')[1]}</td>
                  <td className="px-3 py-2.5 font-mono text-amber-400/90 whitespace-nowrap">{evt.srcIp}</td>
                  <td className="px-3 py-2.5 font-mono text-zinc-500 whitespace-nowrap">{evt.srcPort}</td>
                  <td className="px-3 py-2.5 font-mono text-blue-400/80 whitespace-nowrap">{evt.dstIp}</td>
                  <td className="px-3 py-2.5 font-mono text-zinc-500 whitespace-nowrap">{evt.dstPort}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <span className={`inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide ${protocolColors[evt.protocol]}`}>
                      {evt.protocol}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-zinc-300 whitespace-nowrap">{evt.eventType}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap"><SeverityBadge severity={evt.severity} /></td>
                  <td className="px-3 py-2.5 font-mono text-zinc-500 whitespace-nowrap text-[11px]">{evt.asset}</td>
                  <td className="px-3 py-2.5 font-mono text-zinc-600 whitespace-nowrap text-[11px]">{evt.rule}</td>
                  <td className="px-3 py-2.5">
                    <span className="inline-block bg-zinc-800 text-zinc-500 text-[10px] font-mono px-1.5 py-0.5 rounded">
                      {evt.country}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-zinc-600 whitespace-nowrap">{evt.bytes.toLocaleString()}</td>
                  <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="relative group/tip">
                        <button
                          onClick={() => setSelectedEvent(evt)}
                          className="p-1.5 rounded hover:bg-zinc-700 text-zinc-600 hover:text-zinc-300 transition-colors"
                          aria-label="View event details"
                        >
                          <Icon name="EyeIcon" size={13} />
                        </button>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-[10px] text-zinc-300 whitespace-nowrap opacity-0 group-hover/tip:opacity-100 pointer-events-none z-10">
                          View details
                        </div>
                      </div>
                      <div className="relative group/tip">
                        <button
                          className="p-1.5 rounded hover:bg-zinc-700 text-zinc-600 hover:text-zinc-300 transition-colors"
                          aria-label="Create incident from event"
                        >
                          <Icon name="ExclamationTriangleIcon" size={13} />
                        </button>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-[10px] text-zinc-300 whitespace-nowrap opacity-0 group-hover/tip:opacity-100 pointer-events-none z-10">
                          Create incident
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-800">
          <span className="text-xs text-zinc-600 font-mono">
            Showing {((page - 1) * perPage) + 1}–{Math.min(page * perPage, filtered.length)} of {filtered.length.toLocaleString()} events
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="p-1.5 rounded hover:bg-zinc-800 text-zinc-600 hover:text-zinc-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <Icon name="ChevronDoubleLeftIcon" size={13} />
            </button>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded hover:bg-zinc-800 text-zinc-600 hover:text-zinc-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <Icon name="ChevronLeftIcon" size={13} />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum = Math.max(1, Math.min(page - 2 + i, totalPages - 4 + i));
              return (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={`w-7 h-7 rounded text-xs font-medium transition-colors ${
                    page === pageNum
                      ? 'bg-rose-600 text-white' :'text-zinc-500 hover:bg-zinc-800 hover:text-zinc-300'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded hover:bg-zinc-800 text-zinc-600 hover:text-zinc-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <Icon name="ChevronRightIcon" size={13} />
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              className="p-1.5 rounded hover:bg-zinc-800 text-zinc-600 hover:text-zinc-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <Icon name="ChevronDoubleRightIcon" size={13} />
            </button>
          </div>
        </div>
      </div>

      {/* Detail Drawer */}
      {selectedEvent && (
        <EventDetailDrawer event={selectedEvent} onClose={() => setSelectedEvent(null)} />
      )}
    </div>
  );
}